import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Card,
  CardContent,
  CardHeader,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/table';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { toastSuccess, toastError } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import Breadcrumbs from '@Components/Breadcrumbs';
import Loader from '@Components/Loader';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useEvalSuite, evalKeys } from '@Hooks/useEvals';
import { useSouls } from '@Hooks/useSouls';
import service from '@Service';

export default function SuiteDetails() {
  const { suiteId } = useParams<{ suiteId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: suite, status } = useEvalSuite({ suiteId });
  const { data: souls } = useSouls();
  const [runOpen, setRunOpen] = useState(false);
  const [runningSoulId, setRunningSoulId] = useState<string>('');
  const [runningModel, setRunningModel] = useState<string>('');
  const [runningMaxCost, setRunningMaxCost] = useState<string>('5');
  const [isStarting, setIsStarting] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isCloning, setIsCloning] = useState(false);

  if (status === 'pending') return <Loader size="lg" />;
  if (!suite) {
    return <div className="p-8 text-sm text-zinc-500">Suite not found.</div>;
  }

  const startRun = async () => {
    if (!suite) return;
    const soulId = runningSoulId || suite.defaultSoulId || '';
    if (!soulId) {
      toastError('Pick a soul to run against.');
      return;
    }
    setIsStarting(true);
    try {
      const { runId } = await service.runEvalSuite(suite.id, {
        soulId,
        modelOverride: runningModel || null,
        maxCostUsd: runningMaxCost ? Number(runningMaxCost) : undefined,
      });
      await queryClient.invalidateQueries({ queryKey: evalKeys.suite(suite.id) });
      setRunOpen(false);
      navigate(`/evals/runs/${runId}`);
    } catch (e: any) {
      toastError(`Failed to start run: ${e?.message ?? e}`);
    } finally {
      setIsStarting(false);
    }
  };

  const publish = async () => {
    if (!suite) return;
    setIsPublishing(true);
    try {
      await service.publishEvalSuite(suite.id);
      await queryClient.invalidateQueries({ queryKey: evalKeys.suite(suite.id) });
      toastSuccess('Suite published.');
    } catch (e: any) {
      toastError(`Publish failed: ${e?.message ?? e}`);
    } finally {
      setIsPublishing(false);
    }
  };

  const clone = async () => {
    if (!suite) return;
    setIsCloning(true);
    try {
      const cloned = await service.cloneEvalSuite(suite.id);
      await queryClient.invalidateQueries({ queryKey: evalKeys.suites });
      toastSuccess('Suite cloned.');
      navigate(`/evals/${cloned.id}`);
    } catch (e: any) {
      toastError(`Clone failed: ${e?.message ?? e}`);
    } finally {
      setIsCloning(false);
    }
  };

  const deleteCase = async (caseId: string) => {
    try {
      await service.deleteEvalCase(caseId);
      await queryClient.invalidateQueries({ queryKey: evalKeys.suite(suite.id) });
      toastSuccess('Case removed.');
    } catch (e: any) {
      toastError(`Delete failed: ${e?.message ?? e}`);
    }
  };

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'AI', href: '/evals' },
          { name: 'Evals', href: '/evals' },
          { name: suite.name, href: `/evals/${suite.id}` },
        ]}
      />
      <div className="mt-8 mb-4 flex items-center justify-between">
        <div>
          <Heading>{suite.name}</Heading>
          <div className="mt-2 flex items-center gap-2">
            <Badge variant={suite.status === 'PUBLISHED' ? 'default' : 'outline'}>
              {suite.status}
            </Badge>
            {suite.description && (
              <span className="text-sm text-zinc-500">{suite.description}</span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {suite.status === 'DRAFT' ? (
            <Button variant="outline" onClick={publish} loading={isPublishing}>
              Publish
            </Button>
          ) : (
            <Button variant="outline" onClick={clone} loading={isCloning}>
              Clone to draft
            </Button>
          )}
          <Dialog open={runOpen} onOpenChange={setRunOpen}>
            <DialogTrigger asChild>
              <Button>Run Suite</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Run "{suite.name}"</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Soul</label>
                  <select
                    className="border-input bg-background mt-1 flex h-9 w-full rounded-md border px-3 py-1 text-sm"
                    value={runningSoulId || suite.defaultSoulId || ''}
                    onChange={(e) => setRunningSoulId(e.target.value)}
                  >
                    <option value="">— pick —</option>
                    {(souls ?? []).map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                        {suite.defaultSoulId === s.id ? ' (default)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Model override</label>
                  <Input
                    placeholder="e.g. claude-haiku-4-5 (optional)"
                    value={runningModel}
                    onChange={(e) => setRunningModel(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Max cost (USD)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={runningMaxCost}
                    onChange={(e) => setRunningMaxCost(e.target.value)}
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    Hard cap. Run aborts mid-way if exceeded.
                  </p>
                </div>
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Destructive tools run in dry-run mode — they validate inputs and return synthetic success without firing real SMS/email/DB writes.
                </p>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="link">Cancel</Button>
                </DialogClose>
                <Button onClick={startRun} loading={isStarting}>
                  Start Run
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="mb-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <Heading level="h3">Cases ({suite.cases.length})</Heading>
          {suite.status === 'DRAFT' && (
            <Button variant="outline" asChild>
              <Link to={`/evals/${suite.id}/cases/new`}>Add Case</Link>
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {suite.cases.length === 0 ? (
            <div className="py-8 text-center text-sm text-zinc-500">No cases yet.</div>
          ) : (
            <Table className="w-full">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Weight</TableHead>
                  <TableHead>PII</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suite.cases.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>
                      {suite.status === 'DRAFT' ? (
                        <Button variant="link" className="px-0!" asChild>
                          <Link to={`/evals/${suite.id}/cases/${c.id}/edit`}>{c.name}</Link>
                        </Button>
                      ) : (
                        c.name
                      )}
                    </TableCell>
                    <TableCell>
                      {(c.tags ?? []).map((t) => (
                        <Badge key={t} variant="outline" className="mr-1">
                          {t}
                        </Badge>
                      ))}
                    </TableCell>
                    <TableCell>{c.weight}</TableCell>
                    <TableCell>{c.containsPii && <Badge variant="secondary">PII</Badge>}</TableCell>
                    <TableCell>{new Date(c.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {suite.status === 'DRAFT' && (
                        <div className="flex gap-1">
                          <Button variant="link" size="sm" asChild>
                            <Link to={`/evals/${suite.id}/cases/${c.id}/edit`}>
                              Edit
                            </Link>
                          </Button>
                          <Button
                            variant="dangerLink"
                            size="sm"
                            onClick={() => deleteCase(c.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <Heading level="h3">Recent Runs ({suite.runs.length})</Heading>
        </CardHeader>
        <CardContent>
          {suite.runs.length === 0 ? (
            <div className="py-8 text-center text-sm text-zinc-500">No runs yet.</div>
          ) : (
            <Table className="w-full">
              <TableHeader>
                <TableRow>
                  <TableHead>Soul</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Pass</TableHead>
                  <TableHead>Cost</TableHead>
                  <TableHead>Started</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suite.runs.map((r) => {
                  const totals: any = r.totals ?? {};
                  const total = (totals.passCount ?? 0) + (totals.failCount ?? 0) + (totals.errorCount ?? 0);
                  return (
                    <TableRow key={r.id}>
                      <TableCell>
                        <Button variant="link" className="px-0!" asChild>
                          <Link to={`/evals/runs/${r.id}`}>{r.soul?.name ?? r.soulId}</Link>
                        </Button>
                      </TableCell>
                      <TableCell>{r.modelOverride ?? <span className="text-zinc-400">default</span>}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            r.status === 'COMPLETED' ? 'default'
                              : r.status === 'FAILED' ? 'destructive'
                              : 'outline'
                          }
                        >
                          {r.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {total > 0
                          ? `${totals.passCount ?? 0}/${total} (${Math.round((totals.passCount ?? 0) / total * 100)}%)`
                          : '—'}
                      </TableCell>
                      <TableCell>${(totals.totalCostUsd ?? 0).toFixed(4)}</TableCell>
                      <TableCell>
                        {r.startedAt ? new Date(r.startedAt).toLocaleString() : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
